import React, { useState, useCallback, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';

import { KModal } from '@/ui-library/components/molecules/KModal';
import { KText } from '@/ui-library/components/text';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import { TIME_ZONE_CONSTANTS } from '@/utils/timeZoneConstants';
import { InterviewDetailsAPI } from '@/api/interview-details';

interface TimezoneOption {
  label: string;
  value: string;
  offset: string;
  abbreviation: string;
  fullName: string;
  shortName: string;
}

interface TimezoneSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTimezoneSelect: (timezone: string) => void;
  isLoading?: boolean;
}

const createTimezoneOffset = (timezoneKey: string): string => {
  const offsetInMinutes = moment().tz(timezoneKey).utcOffset();
  const hours = Math.floor(Math.abs(offsetInMinutes) / 60);
  const minutes = Math.abs(offsetInMinutes) % 60;
  const sign = offsetInMinutes < 0 ? '-' : '+';

  return `GMT${sign}${String(hours).padStart(2, '0')}:${String(
    minutes
  ).padStart(2, '0')}`;
};

const createTimezoneOption = (timezoneKey: string): TimezoneOption => {
  const { fullName, shortName, abbreviation } =
    TIME_ZONE_CONSTANTS[timezoneKey] || {};
  const liveOffset = createTimezoneOffset(timezoneKey);

  return {
    label: `${fullName} (${liveOffset}) - ${shortName}`,
    value: timezoneKey,
    offset: liveOffset,
    abbreviation: abbreviation || '',
    fullName: fullName || '',
    shortName: shortName || '',
  };
};

export const TimezoneSelectionModal: React.FC<TimezoneSelectionModalProps> = ({
  isOpen,
  onClose,
  onTimezoneSelect,
  isLoading = false,
}) => {
  const [selectedTimezone, setSelectedTimezone] =
    useState<TimezoneOption | null>(null);

  const { data: { time_zones = {} } = {}, isLoading: isTimezonesLoading } =
    useQuery({
      queryKey: ['getTimeZones'],
      queryFn: () => InterviewDetailsAPI?.getTimeZones(),
      enabled: isOpen,
    });

  const timeZoneOptions = useMemo(() => {
    const timezoneData =
      Object.keys(time_zones || {}).length > 0
        ? time_zones
        : TIME_ZONE_CONSTANTS;

    return Object.entries(timezoneData).map(([key]) =>
      createTimezoneOption(key)
    );
  }, [time_zones]);

  const customFilterOption = useCallback((option, inputValue) => {
    const searchTerm = inputValue.toLowerCase();
    const { abbreviation, fullName, shortName, label } = option?.data || {};

    const searchableFields = [
      abbreviation?.toLowerCase() || '',
      fullName?.toLowerCase() || '',
      shortName?.toLowerCase() || '',
      label?.toLowerCase() || '',
    ];

    return searchableFields.some((field) => field.includes(searchTerm));
  }, []);

  const handleTimezoneChange = useCallback(
    (selected: TimezoneOption | null) => {
      setSelectedTimezone(selected);
    },
    []
  );

  const handleSetTimezone = useCallback(() => {
    if (selectedTimezone?.value) {
      onTimezoneSelect(selectedTimezone.value);
    }
  }, [selectedTimezone, onTimezoneSelect]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <KModal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      ctaAction={handleSetTimezone}
      ctaLabel="Save"
      isCtaLoading={isLoading}
      closeOnOverlayClick={false}
      hideCloseButton={true}
      enableCancelBtn={false}
      closeOnEsc={false}
      ctaButtonProps={{
        isDisabled: !selectedTimezone?.value,
      }}
    >
      <Box>
        <KText size="sm-medium" color="secondary-900" mb="6px">
          Set your timezone
        </KText>
        <KSelect
          placeholder="Search timezone"
          value={selectedTimezone}
          options={timeZoneOptions}
          onChange={handleTimezoneChange}
          filterOption={customFilterOption}
          isSearchable
          menuPlacement="auto"
          isLoading={isTimezonesLoading}
          isDisabled={isTimezonesLoading}
        />
      </Box>
    </KModal>
  );
};
